import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '../environments/environment';
import { AnalyticsService } from './shared/analytics';
import { APP_EVENTS } from './shared/analytics/analytics.events';

/**
 * Global HTTP interceptor that catches 401 (Unauthorized) responses on calls
 * to the vendor backend — the only place where our JWT applies — and treats
 * them as session expiry:
 *   1. Clear stored credentials
 *   2. Show a snackbar telling the user their session expired
 *   3. Redirect to /vendors/login
 *
 * 401s from other backends (e.g. the SSE engine, which uses an API key not
 * a JWT) are let through unchanged — those are config/server-side issues
 * and shouldn't kick the user out.
 *
 * The `redirecting` flag prevents multiple parallel 401s from triggering
 * multiple navigations or stacking snackbars.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private redirecting = false;

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private analytics: AnalyticsService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (
          err.status === 401 &&
          this.isVendorAuthCall(req.url) &&
          !this.redirecting
        ) {
          this.handleSessionExpired();
        }
        return throwError(() => err);
      }),
    );
  }

  private isVendorAuthCall(url: string): boolean {
    // Only treat 401s from the vendor (JWT) backend as session expiry.
    // The SSE backend uses an API key — its 401 means misconfig, not user.
    return url.startsWith(environment.vendorBaseUri);
  }

  private handleSessionExpired(): void {
    this.redirecting = true;

    try {
      this.analytics.track(APP_EVENTS.SESSION_EXPIRED, {
        path: this.router.url,
      });
    } catch { /* never block the redirect */ }

    try {
      localStorage.clear();
    } catch {
      /* ignore quota / privacy-mode errors */
    }
    this.snackBar.open(
      'Your session has expired. Please log in again.',
      'OK',
      { duration: 4000 },
    );
    this.router.navigateByUrl('/vendors/login').finally(() => {
      this.redirecting = false;
    });
  }
}
